import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import './Financeiro.css'

const formatarMoeda = (v) =>
  Number(v) != null && !isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0,00'

const formatarData = (dataStr) => {
  if (!dataStr) return '–'
  const s = String(dataStr).split('T')[0]
  const [y, m, d] = s.split('-')
  return d && m && y ? `${d}/${m}/${y}` : s
}

const STATUS_LABELS = { 0: 'Novo', 1: 'Autorizado', 2: 'Rejeitado', 3: 'Baixado' }

const STATUS_NOVO = 0
const STATUS_AUTORIZADO = 1
const STATUS_BAIXADO = 3

const Financeiro = ({ onBack, userRole, userEmpresa, userId, permissoesMenusCache = {} }) => {
  const canAutorizar = userRole === 'administrador' || permissoesMenusCache.financeiro_autorizar === true
  const canBaixar = userRole === 'administrador' || permissoesMenusCache.financeiro_baixar === true
  const canPago = userRole === 'administrador' || permissoesMenusCache.financeiro_pago === true
  const hoje = new Date()
  const mesAnoPadrao = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
  const [dataInicio, setDataInicio] = useState(hoje.toISOString().split('T')[0])
  const [dataFim, setDataFim] = useState(hoje.toISOString().split('T')[0])
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [movimentacoes, setMovimentacoes] = useState([])
  const [tiposEntradaSaida, setTiposEntradaSaida] = useState([])
  const [statusList, setStatusList] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showHistoricoModal, setShowHistoricoModal] = useState(false)
  const [historicoMov, setHistoricoMov] = useState([])
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [actionMsg, setActionMsg] = useState({ text: '', type: '' })
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    id_tipo_entrada_saida: '',
    quantidade: 1,
    valor_unitario: '',
    data_cadastro: hoje.toISOString().split('T')[0],
    observacao: '',
    status: 0,
    pago: false
  })

  const loadTiposEntradaSaida = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_entrada_saida')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome')
      if (error) throw error
      setTiposEntradaSaida(data || [])
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
    }
  }, [])

  const loadMovimentacaoStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('movimentacao_status')
        .select('id, codigo, descricao')
        .order('codigo')
      if (error) throw error
      setStatusList(data || [])
    } catch (err) {
      console.error('Erro ao carregar status:', err)
    }
  }, [])

  const loadUsuarios = useCallback(async () => {
    try {
      let query = supabase.from('usuarios_sistema').select('id, nome').order('nome')
      if (userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }
      const { data, error } = await query
      if (error) throw error
      setUsuarios(data || [])
    } catch (err) {
      console.error('Erro ao carregar usuários:', err)
    }
  }, [userEmpresa?.id])

  const loadMovimentacoes = useCallback(async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('movimentacao')
        .select(`
          id,
          id_responsavel,
          id_tipo_entrada_saida,
          quantidade,
          valor_unitario,
          total,
          data_cadastro,
          status,
          pago,
          observacao,
          tipo_entrada_saida(id, nome),
          usuarios_sistema(id, nome)
        `)
        .gte('data_cadastro', dataInicio)
        .lte('data_cadastro', dataFim)
        .order('data_cadastro', { ascending: false })
        .order('id', { ascending: false })
      if (filtroTipo !== '') {
        const idTipo = parseInt(filtroTipo, 10)
        if (!isNaN(idTipo)) query = query.eq('id_tipo_entrada_saida', idTipo)
      }
      if (filtroStatus !== '') {
        const s = parseInt(filtroStatus, 10)
        if (!isNaN(s)) query = query.eq('status', s)
      }
      if (userEmpresa?.id) {
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', userEmpresa.id)
        const ids = (usuariosEmpresa || []).map((u) => u.id)
        if (ids.length > 0) {
          query = query.in('id_responsavel', ids)
        } else {
          setMovimentacoes([])
          setLoading(false)
          return
        }
      }
      const { data, error } = await query
      if (error) throw error
      setMovimentacoes(data || [])
    } catch (err) {
      console.error('Erro ao carregar movimentações:', err)
      setMovimentacoes([])
    } finally {
      setLoading(false)
    }
  }, [dataInicio, dataFim, filtroTipo, filtroStatus, userEmpresa?.id])

  useEffect(() => {
    loadTiposEntradaSaida()
    loadMovimentacaoStatus()
    loadUsuarios()
  }, [loadTiposEntradaSaida, loadMovimentacaoStatus, loadUsuarios])

  useEffect(() => {
    if (tiposEntradaSaida.length > 0 && !formData.id_tipo_entrada_saida) {
      setFormData((f) => ({ ...f, id_tipo_entrada_saida: tiposEntradaSaida[0].id }))
    }
  }, [tiposEntradaSaida])

  useEffect(() => {
    loadMovimentacoes()
  }, [loadMovimentacoes])

  const resetFormRegistro = useCallback(() => {
    setFormData({
      id_tipo_entrada_saida: tiposEntradaSaida[0]?.id ?? '',
      quantidade: 1,
      valor_unitario: '',
      data_cadastro: new Date().toISOString().split('T')[0],
      observacao: '',
      status: 0,
      pago: false
    })
    setMsg({ text: '', type: '' })
  }, [tiposEntradaSaida])

  const totalGeral = movimentacoes.reduce((s, m) => s + Number(m.total || 0), 0)

  const openModalNova = () => {
    setEditingId(null)
    resetFormRegistro()
    setShowModal(true)
  }

  const openModalEditar = (m) => {
    setEditingId(m.id)
    setFormData({
      id_tipo_entrada_saida: m.id_tipo_entrada_saida,
      quantidade: m.quantidade ?? 1,
      valor_unitario: m.valor_unitario ?? '',
      data_cadastro: (m.data_cadastro || '').toString().split('T')[0],
      observacao: m.observacao ?? '',
      status: m.status ?? 0,
      pago: !!m.pago
    })
    setMsg({ text: '', type: '' })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingId(null)
  }

  const getStatusIdByCodigo = (codigo) => {
    const s = statusList.find((x) => x.codigo === codigo)
    return s ? s.id : null
  }

  const aplicarStatus = async (idMov, novoStatus, codigoHistorico) => {
    setActionMsg({ text: '', type: '' })
    try {
      const { error } = await supabase
        .from('movimentacao')
        .update({ status: novoStatus })
        .eq('id', idMov)
      if (error) throw error
      const statusId = getStatusIdByCodigo(codigoHistorico)
      if (statusId && userId) {
        await supabase.from('mov_historico').insert({
          fatura: idMov,
          id_responsavel: userId,
          tipo_mov: statusId,
          data_mov: new Date().toISOString()
        })
      }
      loadMovimentacoes()
    } catch (err) {
      console.error('Erro ao alterar status:', err)
      setActionMsg({ text: err.message || 'Erro ao alterar status.', type: 'error' })
    }
  }

  const handleAutorizar = (m) => {
    aplicarStatus(m.id, STATUS_AUTORIZADO, STATUS_AUTORIZADO)
  }

  const handleBaixar = (m) => {
    aplicarStatus(m.id, STATUS_BAIXADO, STATUS_BAIXADO)
  }

  const handleMarcarPago = async (m) => {
    setActionMsg({ text: '', type: '' })
    try {
      const { error } = await supabase
        .from('movimentacao')
        .update({ pago: true })
        .eq('id', m.id)
      if (error) throw error
      loadMovimentacoes()
    } catch (err) {
      console.error('Erro ao marcar como pago:', err)
      setActionMsg({ text: err.message || 'Erro ao marcar como pago.', type: 'error' })
    }
  }

  const handleExcluir = async (m) => {
    const confirma = window.confirm(
      `Excluir a movimentação #${m.id} (${m.tipo_entrada_saida?.nome ?? ''} - R$ ${formatarMoeda(m.total)})? Esta ação também remove os registros do histórico.`
    )
    if (!confirma) return
    setActionMsg({ text: '', type: '' })
    try {
      const { error } = await supabase
        .from('movimentacao')
        .delete()
        .eq('id', m.id)
      if (error) throw error
      setActionMsg({ text: 'Movimentação excluída. O histórico foi removido junto.', type: 'success' })
      loadMovimentacoes()
      setTimeout(() => setActionMsg({ text: '', type: '' }), 4000)
    } catch (err) {
      console.error('Erro ao excluir:', err)
      setActionMsg({ text: err.message || 'Erro ao excluir movimentação.', type: 'error' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const idTipo = formData.id_tipo_entrada_saida ? parseInt(formData.id_tipo_entrada_saida, 10) : null
    const qtd = parseInt(formData.quantidade, 10) || 1
    const valorUnit = formData.valor_unitario ? parseFloat(String(formData.valor_unitario).replace(',', '.')) : 0
    if (!idTipo) {
      setMsg({ text: 'Selecione o tipo de entrada/saída.', type: 'error' })
      return
    }
    if (!formData.data_cadastro) {
      setMsg({ text: 'Informe a data.', type: 'error' })
      return
    }
    if (qtd < 1) {
      setMsg({ text: 'Quantidade deve ser maior que zero.', type: 'error' })
      return
    }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      if (editingId) {
        const { data: movAtual } = await supabase
          .from('movimentacao')
          .select('status')
          .eq('id', editingId)
          .single()
        if (movAtual && Number(movAtual.status) !== STATUS_NOVO) {
          setMsg({ text: 'Só é possível editar movimentação com status Novo.', type: 'error' })
          setSaving(false)
          return
        }
        const { error: errUpdate } = await supabase
          .from('movimentacao')
          .update({
            id_tipo_entrada_saida: idTipo,
            quantidade: qtd,
            valor_unitario: valorUnit,
            data_cadastro: formData.data_cadastro,
            observacao: formData.observacao?.trim() || null
          })
          .eq('id', editingId)
        if (errUpdate) throw errUpdate
      } else {
        if (!userId) {
          setMsg({ text: 'Usuário não identificado.', type: 'error' })
          setSaving(false)
          return
        }
        const { data: inserted, error: errInsert } = await supabase
          .from('movimentacao')
          .insert([
            {
              id_responsavel: userId,
              id_tipo_entrada_saida: idTipo,
              quantidade: qtd,
              valor_unitario: valorUnit,
              data_cadastro: formData.data_cadastro,
              status: 0,
              pago: false,
              observacao: formData.observacao?.trim() || null
            }
          ])
          .select('id')
          .single()
        if (errInsert) throw errInsert
        const statusNovoId = getStatusIdByCodigo(0)
        if (inserted?.id && statusNovoId) {
          await supabase.from('mov_historico').insert({
            fatura: inserted.id,
            id_responsavel: userId,
            tipo_mov: statusNovoId,
            data_mov: new Date().toISOString()
          })
        }
        resetFormRegistro()
        setActionMsg({ text: 'Movimentação registrada com sucesso.', type: 'success' })
        setTimeout(() => setActionMsg({ text: '', type: '' }), 3000)
      }
      if (editingId) closeModal()
      loadMovimentacoes()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao salvar movimentação.', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const abrirHistorico = async (idMov) => {
    try {
      const { data, error } = await supabase
        .from('mov_historico')
        .select(`
          id,
          fatura,
          id_responsavel,
          tipo_mov,
          data_mov,
          observacao,
          usuarios_sistema(nome),
          movimentacao_status(descricao)
        `)
        .eq('fatura', idMov)
        .order('data_mov', { ascending: false })
      if (error) throw error
      setHistoricoMov(data || [])
      setShowHistoricoModal(true)
    } catch (err) {
      console.error('Erro ao carregar histórico:', err)
    }
  }

  return (
    <div className="financeiro">
      <div className="financeiro-header">
        <button type="button" className="financeiro-btn-back" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="financeiro-title">Movimentações Entrada/Saída</h1>
      </div>

      {/* Card Registrar - oculto durante edição (usa modal) */}
      {!editingId && (
      <section className="financeiro-card-registro" aria-label="Registrar movimentação">
        <h2 className="financeiro-card-registro-titulo">
          <span className="financeiro-card-registro-icone">📝</span>
          Registrar Entrada/Saída
        </h2>
        <form onSubmit={handleSubmit} className="financeiro-form-registro">
          <div className="financeiro-form-registro-grid">
            <label className="financeiro-label">
              Tipo
              <select
                className="financeiro-input"
                value={formData.id_tipo_entrada_saida}
                onChange={(e) => setFormData((f) => ({ ...f, id_tipo_entrada_saida: e.target.value }))}
                required
              >
                <option value="">Selecione o tipo</option>
                {tiposEntradaSaida.map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </label>
            <label className="financeiro-label">
              Quantidade
              <input
                type="number"
                min={1}
                className="financeiro-input"
                value={formData.quantidade}
                onChange={(e) => setFormData((f) => ({ ...f, quantidade: e.target.value }))}
                required
              />
            </label>
            <label className="financeiro-label">
              Valor unitário (R$)
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                className="financeiro-input"
                value={formData.valor_unitario}
                onChange={(e) => setFormData((f) => ({ ...f, valor_unitario: e.target.value }))}
              />
            </label>
            <label className="financeiro-label">
              Data
              <input
                type="date"
                className="financeiro-input"
                value={formData.data_cadastro}
                onChange={(e) => setFormData((f) => ({ ...f, data_cadastro: e.target.value }))}
                required
              />
            </label>
          </div>
          <div className="financeiro-form-registro-rodape">
            <label className="financeiro-label financeiro-label-obs">
              Observação
              <input
                type="text"
                className="financeiro-input"
                placeholder="Ex: descrição ou referência"
                value={formData.observacao}
                onChange={(e) => setFormData((f) => ({ ...f, observacao: e.target.value }))}
              />
            </label>
            <button type="submit" className="financeiro-btn-registrar" disabled={saving}>
              {saving ? 'Registrando...' : 'Registrar'}
            </button>
            <button type="button" className="financeiro-btn-limpar" onClick={resetFormRegistro}>
              Limpar
            </button>
          </div>
        </form>
      </section>
      )}

      {/* Filtros e total */}
      <div className="financeiro-filtros">
        <label className="financeiro-label">
          De
          <input
            type="date"
            className="financeiro-input"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </label>
        <label className="financeiro-label">
          Até
          <input
            type="date"
            className="financeiro-input"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </label>
        <label className="financeiro-label">
          Tipo
          <select
            className="financeiro-select"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="">Todos</option>
            {tiposEntradaSaida.map((t) => (
              <option key={t.id} value={t.id}>{t.nome}</option>
            ))}
          </select>
        </label>
        <label className="financeiro-label">
          Status
          <select
            className="financeiro-select"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="0">Novo</option>
            <option value="1">Autorizado</option>
            <option value="2">Rejeitado</option>
            <option value="3">Baixado</option>
          </select>
        </label>
      </div>

      <div className="financeiro-total">
        Total no período: <strong>R$ {formatarMoeda(totalGeral)}</strong>
      </div>

      {actionMsg.text && (
        <div className={`financeiro-msg financeiro-msg-${actionMsg.type}`} role="alert">
          {actionMsg.text}
        </div>
      )}

      {loading ? (
        <p className="financeiro-loading">Carregando...</p>
      ) : (
        <div className="financeiro-tabela-wrap">
          <table className="financeiro-tabela">
            <thead>
              <tr>
                <th>#</th>
                <th>Data</th>
                <th>Responsável</th>
                <th>Tipo</th>
                <th>Qtd</th>
                <th>Valor unit.</th>
                <th>Total</th>
                <th>Status</th>
                <th>Pago</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {movimentacoes.length === 0 ? (
                <tr>
                  <td colSpan={10} className="financeiro-empty">
                    Nenhuma movimentação no período.
                  </td>
                </tr>
              ) : (
                movimentacoes.map((m) => (
                  <tr key={m.id}>
                    <td>{m.id}</td>
                    <td>{formatarData(m.data_cadastro)}</td>
                    <td>{m.usuarios_sistema?.nome ?? '–'}</td>
                    <td>{m.tipo_entrada_saida?.nome ?? '–'}</td>
                    <td>{m.quantidade}</td>
                    <td>R$ {formatarMoeda(m.valor_unitario)}</td>
                    <td>R$ {formatarMoeda(m.total)}</td>
                    <td>
                      <span className={`financeiro-status status-${m.status}`}>
                        {STATUS_LABELS[m.status] ?? m.status}
                      </span>
                    </td>
                    <td>{m.pago ? 'Sim' : 'Não'}</td>
                    <td>
                      {Number(m.status) === STATUS_NOVO && (
                        <button
                          type="button"
                          className="financeiro-btn-acao"
                          onClick={() => openModalEditar(m)}
                        >
                          Editar
                        </button>
                      )}
                      {Number(m.status) === STATUS_NOVO && canAutorizar && (
                        <button
                          type="button"
                          className="financeiro-btn-acao financeiro-btn-autorizar"
                          onClick={() => handleAutorizar(m)}
                        >
                          Autorizar
                        </button>
                      )}
                      {Number(m.status) === STATUS_AUTORIZADO && canBaixar && (
                        <button
                          type="button"
                          className="financeiro-btn-acao financeiro-btn-baixar"
                          onClick={() => handleBaixar(m)}
                        >
                          Baixar
                        </button>
                      )}
                      {(Number(m.status) === STATUS_AUTORIZADO || Number(m.status) === STATUS_BAIXADO) && !m.pago && canPago && (
                        <button
                          type="button"
                          className="financeiro-btn-acao financeiro-btn-pago"
                          onClick={() => handleMarcarPago(m)}
                        >
                          Pago
                        </button>
                      )}
                      <button
                        type="button"
                        className="financeiro-btn-acao"
                        onClick={() => abrirHistorico(m.id)}
                      >
                        Histórico
                      </button>
                      <button
                        type="button"
                        className="financeiro-btn-acao financeiro-btn-excluir"
                        onClick={() => handleExcluir(m)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Nova/Editar movimentação */}
      {showModal && (
        <div className="financeiro-modal-overlay" onClick={closeModal}>
          <div className="financeiro-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="financeiro-modal-title">
              {editingId ? 'Editar movimentação' : 'Nova movimentação'}
            </h2>
            {msg.text && (
              <div className={`financeiro-msg financeiro-msg-${msg.type}`}>{msg.text}</div>
            )}
            <form onSubmit={handleSubmit}>
              <label className="financeiro-label">
                Tipo entrada/saída *
                <select
                  className="financeiro-select financeiro-select-full"
                  value={formData.id_tipo_entrada_saida}
                  onChange={(e) => setFormData((f) => ({ ...f, id_tipo_entrada_saida: e.target.value }))}
                  required
                >
                  <option value="">Selecione</option>
                  {tiposEntradaSaida.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </select>
              </label>
              <div className="financeiro-row">
                <label className="financeiro-label">
                  Quantidade *
                  <input
                    type="number"
                    min={1}
                    className="financeiro-input"
                    value={formData.quantidade}
                    onChange={(e) => setFormData((f) => ({ ...f, quantidade: e.target.value }))}
                    required
                  />
                </label>
                <label className="financeiro-label">
                  Valor unitário *
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    className="financeiro-input"
                    value={formData.valor_unitario}
                    onChange={(e) => setFormData((f) => ({ ...f, valor_unitario: e.target.value }))}
                  />
                </label>
              </div>
              <label className="financeiro-label">
                Data cadastro *
                <input
                  type="date"
                  className="financeiro-input financeiro-input-full"
                  value={formData.data_cadastro}
                  onChange={(e) => setFormData((f) => ({ ...f, data_cadastro: e.target.value }))}
                  required
                />
              </label>
              {editingId && (
                <p className="financeiro-edit-info small text-muted">
                  Status e Pago são alterados pelos botões Autorizar, Baixar e Pago na lista.
                </p>
              )}
              <label className="financeiro-label">
                Observação
                <textarea
                  className="financeiro-textarea"
                  rows={2}
                  value={formData.observacao}
                  onChange={(e) => setFormData((f) => ({ ...f, observacao: e.target.value }))}
                />
              </label>
              <div className="financeiro-modal-buttons">
                <button type="button" className="financeiro-btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="financeiro-btn-save" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Histórico */}
      {showHistoricoModal && (
        <div className="financeiro-modal-overlay" onClick={() => setShowHistoricoModal(false)}>
          <div className="financeiro-modal financeiro-modal-hist" onClick={(e) => e.stopPropagation()}>
            <h2 className="financeiro-modal-title">Histórico da movimentação</h2>
            <div className="financeiro-hist-list">
              {historicoMov.length === 0 ? (
                <p className="financeiro-empty">Nenhum registro no histórico.</p>
              ) : (
                historicoMov.map((h) => (
                  <div key={h.id} className="financeiro-hist-item">
                    <span className="financeiro-hist-data">{formatarData(h.data_mov)}</span>
                    <span className="financeiro-hist-user">{h.usuarios_sistema?.nome ?? '–'}</span>
                    <span className="financeiro-hist-status">
                      {h.movimentacao_status?.descricao ?? h.tipo_mov}
                    </span>
                  </div>
                ))
              )}
            </div>
            <div className="financeiro-modal-buttons">
              <button
                type="button"
                className="financeiro-btn-cancel"
                onClick={() => setShowHistoricoModal(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Financeiro
